import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Paper, Typography, Box, Card, CardContent, Avatar, List, ListItem, ListItemIcon, ListItemText, Alert, Chip, Divider, IconButton, Grow } from '@mui/material';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import GroupIcon from '@mui/icons-material/Group';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import axios from 'axios';
import Loading from '../components/Loading';
import API_URL from '../config';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

function SupportDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const firstName = (localStorage.getItem('full_name') || 'User').split(' ')[0];
  
  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${API_URL}/it-projects/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchStats();
  }, []);

  if (!stats) return <Loading message="Loading IT Dashboard..." />;

  // Doughnut Data (Vibrant Colors)
  const doughnutData = {
    labels: Object.keys(stats.status_distribution),
    datasets: [
      {
        data: Object.values(stats.status_distribution),
        backgroundColor: ['#F59E0B', '#94A3B8', '#EF4444', '#10B981', '#3B82F6'],
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverOffset: 4
      },
    ],
  };

  // Bar Data (Clean Blue)
  // Using avg_progress or other metrics if available. For now just avg_progress
  const barData = {
    labels: ['Average Progress'],
    datasets: [
      {
        label: 'Percentage',
        data: [stats.avg_progress],
        backgroundColor: '#6366F1',
        borderRadius: 6,
        barThickness: 40,
      },
    ],
  };


  // Clean Stat Card Component
  const StatCard = ({ title, value, subtitle, icon, color }) => (
    <Card elevation={0} sx={{ height: '100%', p: 3, borderRadius: 4, bgcolor: 'white', border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="caption" fontWeight="800" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>{title}</Typography>
            <Box sx={{ color: color, p: 1, borderRadius: 3, bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {icon}
            </Box>
        </Box>
        <Typography variant="h4" fontWeight="800" sx={{ color: '#1e293b', mb: 1 }}>{value}</Typography>
        <Typography variant="caption" sx={{ color: subtitle?.includes('Risk') ? '#F59E0B' : (subtitle?.includes('Active') ? '#10B981' : '#64748b'), fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {subtitle}
        </Typography>
    </Card>
  );

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      
      {/* Modern Gradient Header */}
      <Grow in={true} timeout={500}>
        <Box sx={{ 
            mb: 4, 
            p: 4, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3,
            mt: 2
        }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                    🖥️ IT Projects Dashboard
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                    Welcome back, {firstName}. Overview of IT projects, active tickets, and operations status.
                </Typography>
            </Box>
            
            {/* Decorative background circles */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        </Box>
      </Grow>

      {/* Stats Row */}
      <Grid container spacing={3} sx={{ mb: 7 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Projects" 
            value={stats.total_projects} 
            subtitle="IT Projects" 
            icon={<FolderIcon fontSize="medium" />} 
            color="#3B82F6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Active" 
            value={stats.active_projects} 
            subtitle="Ongoing" 
            icon={<CheckCircleIcon fontSize="medium" />} 
            color="#10B981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="At Risk" 
            value={stats.at_risk} 
            subtitle="Overdue" 
            icon={<WarningIcon fontSize="medium" />} 
            color="#F59E0B"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Avg Progress" 
            value={`${stats.avg_progress}%`} 
            subtitle="Overall Completion" 
            icon={<CheckIcon fontSize="medium" />} 
            color="#6366F1"
          />
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={4}>
        
        {/* Left Column: Project Performance (2/3 width) */}
        <Grid item xs={12} lg={8}>
            
            <Card elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: 'white', border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', height: '100%' }}>
                
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight="800" sx={{ color: '#1e293b' }}>Status Distribution</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>Current condition status across all IT projects</Typography>
                </Box>

                <Grid container spacing={4}>
                    
                    {/* Doughnut + Summary */}
                    <Grid item xs={12} md={6} sx={{ mb: 4 }}>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12}>
                                <Box sx={{ height: 220, display: 'flex', justifyContent: 'center' }}>
                                    <Doughnut 
                                        data={doughnutData} 
                                        options={{ 
                                            cutout: '75%', 
                                            plugins: { legend: { display: false } } 
                                        }} 
                                    />
                                </Box>
                            </Grid>
                        
                        </Grid>
                    </Grid>

                    {/* Bar Chart Section */}
                    <Grid item xs={12} md={6}>
                         <Typography variant="subtitle2" fontWeight="800" sx={{ mb: 2, color: '#1e293b' }}>Average Progress</Typography>
                         <Box sx={{ height: 250, p: 2, bgcolor: '#f8fafc', borderRadius: 3, border: '1px dashed #e2e8f0' }}>
                            <Bar 
                                data={barData} 
                                options={{ 
                                    responsive: true, 
                                    maintainAspectRatio: false,
                                    scales: { 
                                        y: { display: true, beginAtZero: true, max: 100, grid: { borderDash: [4, 4], color: '#e2e8f0' } }, 
                                        x: { grid: { display: false } } 
                                    },
                                    plugins: { legend: { display: false } }
                                }} 
                            />
                         </Box>
                    </Grid>

                </Grid>
            </Card>

        </Grid>

        {/* Right Column: Upcoming Milestones (1/3 width) */}
        <Grid item xs={12} lg={4}>
          <Card elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: 'white', border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', height: '100%' }}>                   
            {/* Upcoming Milestones */}
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="800" sx={{ color: '#1e293b' }}>Upcoming Milestones</Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>Milestones due in next 7 days</Typography>
                    </Box>
                    {stats.weekly_schedule && stats.weekly_schedule.length > 0 && (
                        <Chip label={stats.weekly_schedule.length} size="small" sx={{ bgcolor: '#fee2e2', color: '#ef4444', fontWeight: 'bold' }} />
                    )}
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {stats.weekly_schedule && stats.weekly_schedule.length > 0 ? (
                        stats.weekly_schedule.map((entry) => (
                            <Box 
                                key={entry.title + entry.id} 
                                onClick={() => navigate(`/it-projects/${entry.id}`)}
                                sx={{ 
                                    p: 2.5, 
                                    borderRadius: 3, 
                                    bgcolor: '#fffbeb', 
                                    border: '1px solid #fed7aa',
                                    transition: 'all 0.2s',
                                    cursor: 'pointer',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(234, 88, 12, 0.1)' }
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Chip 
                                        label={entry.status} 
                                        size="small" 
                                        sx={{ 
                                            height: 22,
                                            fontSize: '0.7rem',
                                            fontWeight: 800,
                                            bgcolor: 'white',
                                            color: '#ea580c',
                                            border: '1px solid #ffedd5'
                                        }} 
                                    />
                                </Box>
                                
                                <Typography variant="subtitle2" fontWeight="800" sx={{ color: '#9a3412', mb: 0.5 }}>
                                    {entry.title}
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ color: '#c2410c', mb: 2, lineHeight: 1.4, fontWeight: 600 }}>
                                    Project: {entry.project_name}
                                </Typography>

                                <Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'rgba(254, 215, 170, 0.5)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" display="block" sx={{ color: '#9a3412', opacity: 0.7, fontWeight: 700 }}>Due Date</Typography>
                                    <Typography variant="caption" fontWeight="800" sx={{ color: '#9a3412' }}>
                                        {new Date(entry.start_time).toLocaleDateString()}
                                    </Typography>
                                </Box>
                            </Box>
                        ))
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 4, border: '2px dashed #e2e8f0' }}>
                            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>No upcoming milestones.</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
          </Card>
        </Grid>

      </Grid>

      {/* Risk Projects Grid */}
      <Grid container spacing={4} sx={{ mt: 2 }}>
        
            {/* Risk Projects (Alerts) */}
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: 'white', border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', height: '100%' }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight="800" sx={{ color: '#1e293b' }}>Risk Projects</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>IT Projects requiring immediate attention</Typography>
                </Box>
                {stats.alerts && stats.alerts.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {stats.alerts.map((alert, index) => (
                            <Box 
                                key={index} 
                                onClick={() => navigate(`/it-projects/${alert.id}`)}
                                sx={{ 
                                    p: 2.5, 
                                    borderRadius: 3, 
                                    border: '1px solid #fecaca',
                                    bgcolor: '#fef2f2',
                                    transition: 'all 0.2s',
                                    cursor: 'pointer',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.1)' }
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                    <Typography variant="subtitle2" fontWeight="800" sx={{ color: '#991b1b' }}>Project Alert</Typography>
                                    <Chip 
                                        label="Overdue" 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: '#ef4444', 
                                            color: 'white', 
                                            fontSize: '0.7rem',
                                            fontWeight: 800, 
                                            borderRadius: 1.5,
                                            height: 22
                                        }} 
                                    />
                                </Box>
                                
                                <Typography variant="body2" sx={{ color: '#b91c1c', mb: 2, fontWeight: 500, lineHeight: 1.4 }}>
                                    {alert.message}
                                </Typography>

                                <Box sx={{ pt: 1.5, borderTop: '1px solid rgba(254, 202, 202, 0.5)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" fontWeight="800" sx={{ color: '#991b1b', opacity: 0.7 }}>Due Date:</Typography>
                                    <Typography variant="caption" fontWeight="800" sx={{ color: '#991b1b' }}>{alert.date}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ p: 4, borderRadius: 4, border: '2px dashed #e2e8f0', textAlign: 'center', bgcolor: '#f8fafc' }}>
                         <CheckCircleIcon sx={{ fontSize: 40, mb: 1, color: '#94a3b8' }} />
                         <Typography variant="body2" fontWeight="600" sx={{ color: '#64748b' }}>No IT projects currently at risk.</Typography>
                    </Box>
                )}
              </Card>
            </Grid>

            {/* Active Tickets */}
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: 'white', border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', height: '100%' }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight="800" sx={{ color: '#1e293b' }}>Active Tickets</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>Your assigned ticket queue</Typography>
                </Box>
                 {stats.my_tickets && stats.my_tickets.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {stats.my_tickets.map((ticket) => (
                            <Box 
                                key={ticket.id} 
                                onClick={() => navigate(`/tickets/${ticket.id}`)}
                                sx={{ 
                                    p: 2.5, 
                                    borderRadius: 3, 
                                    border: '1px solid #f1f5f9',
                                    bgcolor: 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.02)',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', borderColor: '#e2e8f0' }
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                    <Typography variant="subtitle2" fontWeight="800" sx={{ color: '#1e293b' }}>{ticket.title}</Typography>
                                    <Chip 
                                        label={ticket.status} 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: ticket.status === 'In Progress' ? '#dbeafe' : 
                                                     ticket.status === 'Done' ? '#dcfce7' :
                                                     ticket.status === 'Reopen' ? '#fee2e2' : 
                                                     '#fef3c7', 
                                            color: ticket.status === 'In Progress' ? '#1d4ed8' : 
                                                   ticket.status === 'Done' ? '#15803d' :
                                                   ticket.status === 'Reopen' ? '#b91c1c' : 
                                                   '#b45309', 
                                            fontWeight: '800', 
                                            borderRadius: 1.5,
                                            height: 22,
                                            fontSize: '0.7rem'
                                        }} 
                                    />
                                </Box>
                                
                                <Box sx={{ mb: 2 }}>
                                    <Chip 
                                        label={ticket.type} 
                                        size="small" 
                                        variant="outlined" 
                                        sx={{ 
                                            color: '#ea580c', 
                                            borderColor: '#fed7aa', 
                                            fontWeight: '800', 
                                            borderRadius: 1.5,
                                            height: 22,
                                            fontSize: '0.7rem',
                                            bgcolor: '#fff7ed'
                                        }} 
                                    />
                                </Box>

                                <Typography variant="body2" fontWeight="600" sx={{ color: '#64748b', mb: 2, fontSize: '0.8rem' }}>
                                     ID: #{String(ticket.id).padStart(5, '0')} • Assigned to you
                                </Typography>

                                <Box sx={{ pt: 1.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" fontWeight="800" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>ACTION REQUIRED</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#2563eb' }}>
                                        <Typography variant="caption" fontWeight="800">View Ticket</Typography>
                                        <ArrowForwardIcon sx={{ fontSize: 14 }} />
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ p: 4, borderRadius: 4, border: '2px dashed #e2e8f0', textAlign: 'center', bgcolor: '#f8fafc' }}>
                         <CheckCircleIcon sx={{ fontSize: 40, mb: 1, color: '#94a3b8' }} />
                         <Typography variant="body2" fontWeight="600" sx={{ color: '#64748b' }}>No active tickets.</Typography>
                    </Box>
                )}
              </Card>
            </Grid>

            {/* Expiring Licenses */}
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: 'white', border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', height: '100%' }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight="800" sx={{ color: '#1e293b' }}>Expiring Licenses</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>Renewals due in 30 days</Typography>
                </Box>
                 {stats.expiring_licenses && stats.expiring_licenses.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {stats.expiring_licenses.map((license) => (
                            <Box 
                                key={license.id} 
                                onClick={() => navigate(`/software-licenses/${license.id}`)}
                                sx={{ 
                                    p: 2.5, 
                                    borderRadius: 3, 
                                    border: '1px solid #fed7aa',
                                    bgcolor: '#fffbeb', 
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(249, 115, 22, 0.1)', borderColor: '#fb923c' }
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                    <Typography variant="subtitle2" fontWeight="800" sx={{ color: '#9a3412' }}>{license.name}</Typography>
                                    <Chip 
                                        label="Expiring" 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: '#ea580c', 
                                            color: 'white', 
                                            fontWeight: '800', 
                                            borderRadius: 1.5,
                                            height: 22,
                                            fontSize: '0.7rem'
                                        }} 
                                    />
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                                    <VerifiedUserIcon sx={{ fontSize: 16, color: '#f97316' }} />
                                    <Typography variant="caption" fontWeight="600" sx={{ color: '#9a3412' }}>{license.supplier}</Typography>
                                </Box>

                                <Box sx={{ pt: 1.5, borderTop: '1px solid rgba(254, 215, 170, 0.5)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" fontWeight="800" sx={{ color: '#9a3412', opacity: 0.7 }}>Expires On:</Typography>
                                    <Typography variant="caption" sx={{ color: '#c2410c' }} fontWeight="800">{license.expiry_date}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ p: 4, borderRadius: 4, border: '2px dashed #e2e8f0', textAlign: 'center', bgcolor: '#f8fafc' }}>
                         <VerifiedUserIcon sx={{ fontSize: 40, mb: 1, color: '#94a3b8' }} />
                         <Typography variant="body2" fontWeight="600" sx={{ color: '#64748b' }}>No licenses expiring soon.</Typography>
                    </Box>
                )}
              </Card>
            </Grid>
      </Grid>

    </Box>
  );
}

export default SupportDashboard;
